import React, { useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useSubdomain } from "../providers/SubdomainProvider";
import { useRole, usePermissions } from "../providers/PermissionsProvider";
import { PageLoader } from "./page-loader";
import {
  getSubdomainHopTarget,
  hopToSubdomain,
} from "../utils/subdomainRedirect";

/**
 * LandingRouter - Handles the root `/` route.
 *
 * Logged-out:
 *   - Immediately Auth0-login (no marketing landing on app.* / platform.*).
 *
 * Logged-in:
 *   - If the user's userType doesn't match the current subdomain, bare-hop
 *     to <target>.<rest>/dashboard. AuthenticationGuard there handles the
 *     silent SSO re-auth on the new origin. See `utils/subdomainRedirect.js`
 *     for why we don't use cross-origin `loginWithRedirect`.
 *   - Otherwise, hand off to /dashboard for per-userType routing.
 *
 * `DashboardRedirect` mirrors this hop because the Auth0 callback
 * navigates straight to /dashboard, bypassing LandingRouter.
 */
export const LandingRouter = () => {
  const { subdomain } = useSubdomain();
  const {
    isAuthenticated,
    isLoading: authLoading,
    loginWithRedirect,
  } = useAuth0();
  const { userType, loading: permLoading } = useRole();
  const { isPlatformAdmin } = usePermissions();
  const hopFiredRef = useRef(false);
  const loginFiredRef = useRef(false);

  const hopTarget = isAuthenticated && !permLoading && !isPlatformAdmin
    ? getSubdomainHopTarget(userType, subdomain)
    : null;

  useEffect(() => {
    if (hopTarget && !hopFiredRef.current) {
      hopFiredRef.current = true;
      hopToSubdomain(hopTarget);
    }
  }, [hopTarget]);

  useEffect(() => {
    if (authLoading || isAuthenticated || loginFiredRef.current) return;
    loginFiredRef.current = true;
    loginWithRedirect({
      appState: {
        returnTo: "/dashboard",
      },
    });
  }, [authLoading, isAuthenticated, loginWithRedirect]);

  // Still loading auth or permissions, hopping, or redirecting to login
  if (authLoading || !isAuthenticated || (isAuthenticated && permLoading) || hopTarget) {
    return <PageLoader />;
  }

  return <Navigate to="/dashboard" replace />;
};
